// Generado desde design-system/components/overlays/Tooltip.tsx — no editar; edita el .ts/.tsx y corre build-ds.
import { cloneElement, useEffect, useId, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { EXIT_MS, FloatingLayer, mergeRefs, motionStyle, originFor, useDismiss, usePresence, useReducedMotion, } from './primitives';
/**
 * Tooltip — etiqueta flotante NO interactiva que describe un control (role="tooltip").
 * Abre con hover (tras `delay`) y al instante con foco de teclado; cierra al salir, con blur o Esc.
 * Se puede recorrer con el puntero sin que se cierre (WCAG 1.4.13).
 * rich: título + cuerpo (+ `footer` con dato o atajo). Si necesitas botones o enlaces, usa Popover.
 * describe: añade aria-describedby al disparador (desactívalo si el texto repite el aria-label).
 */
export function Tooltip({ content, title, footer, children, placement = 'top', delay = 450, inline = false, open: openProp, defaultOpen = false, rich = false, describe = true, className, }) {
    const [inner, setInner] = useState(defaultOpen);
    const open = openProp ?? inner;
    const triggerRef = useRef(null);
    const tipRef = useRef(null);
    const timer = useRef(0);
    const uid = useId();
    const reduced = useReducedMotion();
    const { mounted, visible } = usePresence(open, EXIT_MS.tooltip);
    const show = (now) => {
        clearTimeout(timer.current);
        if (now)
            setInner(true);
        else
            timer.current = window.setTimeout(() => setInner(true), delay);
    };
    const hide = () => {
        clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setInner(false), 90);
    };
    useEffect(() => () => clearTimeout(timer.current), []);
    useDismiss({ open: open && openProp === undefined, onClose: () => setInner(false), outside: false });
    const c = children;
    const triggerProps = {
        ref: mergeRefs(triggerRef, c.ref),
        'aria-describedby': describe && open ? [c.props['aria-describedby'], uid].filter(Boolean).join(' ') : c.props['aria-describedby'],
        onPointerEnter: (e) => {
            c.props.onPointerEnter?.(e);
            if (e.pointerType === 'mouse')
                show(false);
        },
        onPointerLeave: (e) => {
            c.props.onPointerLeave?.(e);
            hide();
        },
        onFocus: (e) => {
            c.props.onFocus?.(e);
            let kb = true;
            try {
                kb = e.target.matches(':focus-visible');
            }
            catch {
                kb = true;
            }
            if (kb)
                show(true);
        },
        onBlur: (e) => {
            c.props.onBlur?.(e);
            clearTimeout(timer.current);
            setInner(false);
        },
        onPointerDown: (e) => {
            c.props.onPointerDown?.(e);
            clearTimeout(timer.current);
            setInner(false);
        },
    };
    const trigger = cloneElement(c, triggerProps);
    return (<span className={cn('relative inline-flex', className)}>
      {trigger}
      {mounted && (<FloatingLayer inline={inline} anchorRef={triggerRef} placement={placement} offset={8} layer="tooltip" className="pointer-events-none">
          {({ placement: p }) => (<div ref={tipRef} id={uid} role="tooltip" onPointerEnter={() => clearTimeout(timer.current)} onPointerLeave={hide} className={cn('db-glass-strong pointer-events-auto text-ink', rich ? 'flex w-[280px] flex-col gap-1.5 rounded-md px-4 py-3' : 'w-max max-w-[260px] rounded-sm px-3 py-1.5 text-body-sm font-semibold')} style={motionStyle('tooltip', visible, reduced, originFor(p))}>
              {rich ? (<>
                  {title && <span className="font-display text-title-sm font-medium text-ink">{title}</span>}
                  <span className="text-body-sm text-ink-2">{content}</span>
                  {footer && <span className="mt-1 border-t border-hair pt-2 text-caption text-ink-3">{footer}</span>}
                </>) : (content)}
            </div>)}
        </FloatingLayer>)}
    </span>);
}
